import { FC, ReactNode, useContext, useEffect, useState } from "react";

import { AppleMap } from "capitalrx-components-2.0/lib/FindBestPriceTool/AppleMap";
import { Divider, Icon, Link, Panel, Spinner } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Bold, Semibold, Subheading, Title } from "capitalrx-components-2.0/lib/typography";

import { findBestPricePharmacyGetAPI } from "@/api";
import { FindBestPrice } from "@/api/interfaces";
import { PROVIDER_MANAGEMENT_LAYOUT } from "@/constants/layouts";
import { AuthenticationContext, ThemeSelectorContext } from "@/context";
import { hexToRgbColor } from "@/utils";
import {
  appleMapsUrl,
  CheckCoverageSearch,
  formatDollars,
  formatPhone,
  patientPayAmount,
  pharmacyAddress,
  pharmacyCityStateZip,
  planPayAmount,
  yesNo,
} from "./utils";

import styles from "./CheckCoverage.module.scss";

const DetailItem: FC<{ label: string; children: ReactNode }> = ({ label, children }) => (
  <Stack spacing="none">
    <Body size="mini"><Semibold>{label}</Semibold></Body>
    <Body>{children}</Body>
  </Stack>
);

interface CheckCoverageCostBreakdownProps {
  token: string;
  search: CheckCoverageSearch;
  quantityLabel: string;
  price: FindBestPrice.Price;
  onBack: () => void;
}

export const CheckCoverageCostBreakdown: FC<CheckCoverageCostBreakdownProps> = ({
  token,
  search,
  quantityLabel,
  price,
  onBack,
}) => {
  const { getAuthToken } = useContext(AuthenticationContext);
  const { primary_color } = useContext(ThemeSelectorContext);
  const { sixFull } = useResponsive(PROVIDER_MANAGEMENT_LAYOUT);

  const [pharmacy, setPharmacy] = useState<FindBestPrice.Pharmacy>(price.pharmacy);
  const [isLoadingPharmacy, setIsLoadingPharmacy] = useState(true);

  useEffect(() => {
    let isActive = true;
    setIsLoadingPharmacy(true);

    findBestPricePharmacyGetAPI({ token, provider_id: price.pharmacy.provider_id }, getAuthToken())
      .then((response) => {
        if (isActive) setPharmacy(response);
      })
      .catch((err) => {
        // the price list already carries the core pharmacy fields, so keep showing those
        console.error("Failed to load check coverage pharmacy:", err);
      })
      .finally(() => {
        if (isActive) setIsLoadingPharmacy(false);
      });

    return () => {
      isActive = false;
    };
  }, [token, price]);

  const patientPays = Number(patientPayAmount(price));
  const planPays = Number(planPayAmount(price));
  const driveThru = yesNo(pharmacy.services?.drive_up_window_indicator);
  const immunizations = yesNo(pharmacy.services?.immunizations_provided_indicator);
  const medicaidEnrolled = yesNo(pharmacy.is_medicaid_enrolled);
  const languages = (pharmacy.languages || []).join(", ");
  const hasCoordinates = !!pharmacy.latitude && !!pharmacy.longitude;

  return (
    <Stack spacing="medium-large" className={styles.WideContainer}>
      <Stack spacing="small">
        <Shelf spacing="mini">
          <Icon name="arrow-left" color={hexToRgbColor(primary_color)} />
          <Link
            to="#"
            onClick={(event) => {
              event.preventDefault();
              onBack();
            }}
          >
            Back
          </Link>
          <Shelf.Spacer />
        </Shelf>
        <Title>{search.drug.drug_name}</Title>
      </Stack>

      <Grid>
        <Grid.Span columns={sixFull}>
          <Stack spacing="medium-large">
            <Stack spacing="small">
              <Subheading>Payment Summary</Subheading>
              <Panel testId="check_coverage_payment_summary">
                <Stack spacing="small">
                  <Shelf>
                    <Body size="mini">Total Amount</Body>
                    <Shelf.Spacer />
                    <Body size="mini">{formatDollars(patientPays + planPays)}</Body>
                  </Shelf>
                  <Shelf>
                    <Body size="mini">Plan Pays</Body>
                    <Shelf.Spacer />
                    <Body size="mini">{`-${formatDollars(planPays)}`}</Body>
                  </Shelf>
                  <Divider />
                  <Shelf>
                    <Bold>Patient Pays</Bold>
                    <Shelf.Spacer />
                    <Bold>{formatDollars(patientPays)}</Bold>
                  </Shelf>
                </Stack>
              </Panel>
            </Stack>

            <Stack spacing="small">
              <Subheading>Prescription Details</Subheading>
              <Panel testId="check_coverage_prescription_details">
                <Grid>
                  <Grid.Span columns={6}>
                    <DetailItem label="Drug Name">{search.drug.drug_name}</DetailItem>
                  </Grid.Span>
                  <Grid.Span columns={6}>
                    <DetailItem label="Type">{search.drug.drug_type}</DetailItem>
                  </Grid.Span>
                  <Grid.Span columns={6}>
                    <DetailItem label="Quantity">{quantityLabel}</DetailItem>
                  </Grid.Span>
                  <Grid.Span columns={6}>
                    <DetailItem label="Days Supply">{`${search.days_supply} Days`}</DetailItem>
                  </Grid.Span>
                  <Grid.Span columns={6}>
                    <DetailItem label="Dosage">{search.dosage}</DetailItem>
                  </Grid.Span>
                  <Grid.Span columns={6}>
                    <DetailItem label="Form">{search.dosage_form}</DetailItem>
                  </Grid.Span>
                </Grid>
              </Panel>
            </Stack>
          </Stack>
        </Grid.Span>

        <Grid.Span columns={sixFull}>
          <Stack spacing="small">
            <Subheading>Pharmacy Details</Subheading>
            <Panel testId="check_coverage_pharmacy_details">
              {isLoadingPharmacy ? (
                <Stack alignment="center">
                  <Spinner size="large" />
                </Stack>
              ) : (
                <Stack spacing="medium">
                  <DetailItem label="Billing Provider">{pharmacy.name}</DetailItem>
                  <Grid>
                    <Grid.Span columns={6}>
                      <DetailItem label="Address">
                        <Link to={appleMapsUrl(pharmacy)} openInNewTab>
                          {pharmacyAddress(pharmacy)}
                          <br />
                          {pharmacyCityStateZip(pharmacy)}
                        </Link>
                      </DetailItem>
                    </Grid.Span>
                    <Grid.Span columns={6}>
                      {pharmacy.phone && (
                        <DetailItem label="Phone">
                          <Link to={`tel:${pharmacy.phone}`}>{formatPhone(pharmacy.phone)}</Link>
                        </DetailItem>
                      )}
                    </Grid.Span>
                  </Grid>
                  {(pharmacy.hours || []).length > 0 && (
                    <DetailItem label="Hours">
                      {(pharmacy.hours || []).map((hour) => {
                        const [day, ...time] = hour.split(" - ");
                        return (
                          <Shelf key={hour} spacing="small">
                            <span className={styles.HoursDay}>{day}</span>
                            <span>{time.join(" - ")}</span>
                          </Shelf>
                        );
                      })}
                    </DetailItem>
                  )}
                  <Grid>
                    {driveThru && (
                      <Grid.Span columns={6}>
                        <DetailItem label="Drive Thru">{driveThru}</DetailItem>
                      </Grid.Span>
                    )}
                    {languages && (
                      <Grid.Span columns={6}>
                        <DetailItem label="Languages">{languages}</DetailItem>
                      </Grid.Span>
                    )}
                    {immunizations && (
                      <Grid.Span columns={6}>
                        <DetailItem label="Immunizations">{immunizations}</DetailItem>
                      </Grid.Span>
                    )}
                    {immunizations && (
                      <Grid.Span columns={6}>
                        <DetailItem label="Flu Shots">{immunizations}</DetailItem>
                      </Grid.Span>
                    )}
                    {medicaidEnrolled && (
                      <Grid.Span columns={6}>
                        <DetailItem label="Medicaid Enrolled">{medicaidEnrolled}</DetailItem>
                      </Grid.Span>
                    )}
                  </Grid>
                  {hasCoordinates && (
                    <AppleMap
                      className={styles.PharmacyMap}
                      center={{
                        latitude: parseFloat(pharmacy.latitude!),
                        longitude: parseFloat(pharmacy.longitude!),
                      }}
                      locations={[
                        {
                          latitude: parseFloat(pharmacy.latitude!),
                          longitude: parseFloat(pharmacy.longitude!),
                          title: pharmacy.name,
                        },
                      ]}
                    />
                  )}
                </Stack>
              )}
            </Panel>
          </Stack>
        </Grid.Span>
      </Grid>
    </Stack>
  );
};
